'use client';
/** @jsxImportSource @emotion/react */

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { generateUtilityClasses } from '@mui/material';

import { CoreIcon } from '../core-icon/CoreIcon';
import { IconButton } from '../icon-button/IconButton';
import { InputAdornment } from '../input-adornment/InputAdornment';
import { TextField, type TextFieldProps } from '../text-field/TextField';

type IncludedProps =
  | 'autoFocus'
  | 'classes'
  | 'disabled'
  | 'error'
  | 'FormHelperTextProps'
  | 'fullWidth'
  | 'helperText'
  | 'id'
  | 'InputLabelProps'
  | 'inputProps'
  | 'InputProps'
  | 'inputRef'
  | 'label'
  | 'margin'
  | 'name'
  | 'required'
  | 'size'
  | 'sx';

export interface NumberInputProps extends Pick<TextFieldProps, IncludedProps> {
  /**
   * Default value of the NumberInput. Use when the component is not controlled
   */
  defaultValue?: number;
  /**
   * Maximum allowed value of the NumberInput
   */
  max?: number;
  /**
   * Minimum allowed value of the NumberInput
   */
  min?: number;
  /**
   * Fires each time the NumberInput changes
   */
  onChange?: (value: number) => void;
  /**
   * Fires each time the NumberInput is decremented
   */
  onDecrement?: (
    event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
  ) => void;
  /**
   * Fires each time the NumberInput is incremented
   */
  onIncrement?: (
    event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
  ) => void;
  /**
   * The granularity with which the NumberInput can step through values
   */
  step?: number;
  /**
   * Value of the NumberInput
   */
  value?: number;
}

const numberInputClasses = generateUtilityClasses('NumberInput', [
  'root',
  'input',
  'inputWrapper',
  'startAdornment',
  'endAdornment',
  'startIconButton',
  'endIconButton',
  'startIcon',
  'endIcon',
]);

export { numberInputClasses };

export const NumberInput = forwardRef<HTMLDivElement, NumberInputProps>(
  (
    {
      id,
      name,
      autoFocus,
      classes,
      defaultValue = 0,
      disabled = false,
      error,
      FormHelperTextProps,
      fullWidth,
      helperText,
      InputLabelProps,
      inputRef,
      label,
      margin,
      max,
      min,
      onChange,
      onDecrement,
      onIncrement,
      required,
      size = 'medium',
      step = 1,
      sx = [],
      value: valueFromProps,
    },
    ref
  ): React.JSX.Element => {
    const validateValue = useCallback(
      (value: number) => {
        if (max !== undefined && value > max) {
          return max;
        }
        if (min !== undefined && value < min) {
          return min;
        }
        return value;
      },
      [max, min]
    );

    const isControlled = valueFromProps !== undefined;

    const [internalValue, setInternalValue] = useState(() => {
      return validateValue(valueFromProps ?? defaultValue);
    });

    const value = isControlled ? valueFromProps : internalValue;

    const internalInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(
      inputRef,
      () => internalInputRef.current as HTMLInputElement
    );

    const handleClick = () => {
      if (internalInputRef.current) {
        internalInputRef.current.focus();
      }
    };

    const handleCounterChange = useCallback(
      (newValue: number) => {
        const validatedValue = validateValue(newValue);
        if (!isControlled) {
          setInternalValue(validatedValue);
        }
        onChange?.(validatedValue);
      },
      [onChange, validateValue, isControlled]
    );

    const handleAdd = useCallback(
      (
        event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
      ) => {
        handleCounterChange(value + step);
        onIncrement?.(event);
      },
      [value, handleCounterChange, onIncrement, step]
    );

    const handleSubtract = useCallback(
      (
        event: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLButtonElement>
      ) => {
        handleCounterChange(value - step);
        onDecrement?.(event);
      },
      [value, handleCounterChange, onDecrement, step]
    );

    const handleKeyPress = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.code === 'ArrowUp') {
          event.preventDefault(); // Prevent the page from scrolling
          handleAdd(event);
        }
        if (event.code === 'ArrowDown') {
          event.preventDefault(); // Prevent the page from scrolling
          handleSubtract(event);
        }
      },
      [handleAdd, handleSubtract]
    );

    return (
      <TextField
        id={id}
        name={name}
        ref={ref}
        inputProps={{
          className: numberInputClasses.input,
          onKeyDown: handleKeyPress,
          readOnly: true,
          step,
          style: { textAlign: 'center' },
        }}
        InputProps={{
          className: numberInputClasses.inputWrapper,
          endAdornment: (
            <InputAdornment
              className={numberInputClasses.endAdornment}
              position="end"
            >
              <IconButton
                className={numberInputClasses.endIconButton}
                color={disabled ? undefined : 'primary'}
                disabled={disabled || (max !== undefined && value >= max)}
                size={size}
                tabIndex={-1}
                aria-hidden
                onClick={(event) => {
                  handleClick();
                  handleAdd(event);
                }}
              >
                <CoreIcon name="add" className={numberInputClasses.endIcon} />
              </IconButton>
            </InputAdornment>
          ),
          startAdornment: (
            <InputAdornment
              className={numberInputClasses.startAdornment}
              position="start"
            >
              <IconButton
                className={numberInputClasses.startIconButton}
                color={disabled ? undefined : 'primary'}
                disabled={disabled || (min !== undefined && value <= min)}
                size={size}
                aria-hidden
                tabIndex={-1}
                onClick={(event) => {
                  handleClick();
                  handleSubtract(event);
                }}
              >
                <CoreIcon
                  name="subtract"
                  className={numberInputClasses.startIcon}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={[
          ...(Array.isArray(sx) ? sx : [sx]),
          {
            /* Chrome, Safari, Edge, Opera */
            'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
              {
                margin: 0,
                WebkitAppearance: 'none',
              },
            /* Firefox */
            'input[type=number]': {
              MozAppearance: 'textfield',
            },
            minWidth: (theme) => theme.spacing(18.5),
          },
        ]}
        autoFocus={autoFocus}
        classes={classes}
        className={numberInputClasses.root}
        disabled={disabled}
        error={error}
        FormHelperTextProps={FormHelperTextProps}
        fullWidth={fullWidth}
        helperText={helperText}
        InputLabelProps={InputLabelProps}
        inputRef={internalInputRef}
        label={label}
        margin={margin}
        required={required}
        size={size}
        type="number"
        value={validateValue(value)}
        variant="outlined"
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';
